// Chat and search history in localStorage, encrypted with the offline data
// key when OFFLINE_ENCRYPTION is on (plain mode keeps the legacy keys as
// before). React reads them synchronously in state initializers, so
// preloadSealed() decrypts everything once at boot, before the first render;
// readSealed() then serves from memory and writeSealed() persists in the
// background.
//
// Encrypted mode never writes plaintext: without a key the history lives in
// memory only. A value that does not decrypt (other user on this device, new
// server secret) reads as empty and is replaced by the next write.
import { encryptJson, decryptJson, toB64, fromB64 } from "./odCrypto.js";
import { isEncrypted, getKeys } from "./secure.js";

// name → legacy plain localStorage key (null: never stored in plain form)
export const SEALED_NAMES = {
  chat: "allreco-chat-messages",
  recentSearches: "recentSearches",
  searchTerms: null,
};

const sealedKey = (name) => `od.sealed.${name}`;
const label = (name) => `local|${name}`;

const store = {
  get(k) { try { return globalThis.localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { globalThis.localStorage.setItem(k, v); } catch { /* ignore */ } },
  del(k) { try { globalThis.localStorage.removeItem(k); } catch { /* ignore */ } },
};

const values = new Map();
const writes = new Map(); // name → { running, next }

function parsePlain(raw) {
  try { return raw ? JSON.parse(raw) : undefined; } catch { return undefined; }
}

async function seal(keys, name, value) {
  store.set(sealedKey(name), toB64(await encryptJson(keys, label(name), value)));
}

export async function preloadSealed(names = SEALED_NAMES) {
  values.clear();
  for (const [name, legacy] of Object.entries(names)) {
    if (!isEncrypted()) {
      if (!legacy) continue;
      const v = parsePlain(store.get(legacy));
      if (v !== undefined) values.set(name, v);
      continue;
    }
    const keys = getKeys();
    if (!keys) continue;
    const plain = legacy ? parsePlain(store.get(legacy)) : undefined;
    if (plain !== undefined) {
      // Old plaintext history: move it into the sealed slot, then drop it.
      values.set(name, plain);
      await seal(keys, name, plain);
      store.del(legacy);
      continue;
    }
    const raw = store.get(sealedKey(name));
    if (!raw) continue;
    try { values.set(name, await decryptJson(keys, label(name), fromB64(raw))); } catch { /* unreadable → empty */ }
  }
}

export function readSealed(name, fallback) {
  return values.has(name) ? values.get(name) : fallback;
}

// One write in flight per name; while it runs only the latest value is kept.
export function writeSealed(name, value, names = SEALED_NAMES) {
  values.set(name, value);
  if (!isEncrypted()) {
    if (names[name]) store.set(names[name], JSON.stringify(value));
    return Promise.resolve();
  }
  const w = writes.get(name) || { running: null, next: undefined, pending: false };
  writes.set(name, w);
  w.next = value;
  w.pending = true;
  if (!w.running) {
    w.running = (async () => {
      // Yield first: `running` must be assigned before the loop can clear it.
      await null;
      while (w.pending) {
        const v = w.next;
        w.pending = false;
        const keys = getKeys();
        if (!keys) continue; // locked: memory only
        try { await seal(keys, name, v); } catch { /* ignore */ }
        // removeSealed() ran while this write was encrypting.
        if (!values.has(name)) store.del(sealedKey(name));
      }
      w.running = null;
    })();
  }
  return w.running;
}

export function removeSealed(name, names = SEALED_NAMES) {
  values.delete(name);
  const w = writes.get(name);
  if (w) w.pending = false;
  if (names[name]) store.del(names[name]);
  store.del(sealedKey(name));
}

// Search page URL. Encrypted mode keeps the term out of the URL (and so out of
// the browser's own history files): the URL carries a random id, the id → term
// map is sealed like the history. Such a link only resolves on this device.
const MAX_SEARCH_TERMS = 50;

export function searchPageUrl(term, names = SEALED_NAMES) {
  const t = String(term || "").trim();
  if (!isEncrypted()) return `/route/searchpage?term=${encodeURIComponent(t)}`;
  const id = [...globalThis.crypto.getRandomValues(new Uint8Array(4))]
    .map((b) => b.toString(16).padStart(2, "0")).join("");
  // [id, term] pairs, oldest first (an object would order digit-only ids first).
  const pairs = readSealed("searchTerms", []).slice(-(MAX_SEARCH_TERMS - 1));
  writeSealed("searchTerms", [...pairs, [id, t]], names);
  return `/route/searchpage?s=${id}`;
}

export function searchTermFromParams(params) {
  const term = params.get("term");
  if (term !== null) return term;
  const id = params.get("s");
  const hit = id && readSealed("searchTerms", []).find((p) => p[0] === id);
  return hit ? hit[1] : "";
}
